import React, { useState } from 'react';

interface WordToFind {
  word: string;
  translation: string;
}

interface WordHuntExerciseProps {
  grid: string[][];
  words_to_find?: WordToFind[];
  words?: WordToFind[];  // Alias for words_to_find
  onSubmit: (foundWords: string[]) => void;
}

/**
 * WordHuntExercise Component
 *
 * Users find and select words hidden in a letter grid.
 */
const WordHuntExercise: React.FC<WordHuntExerciseProps> = ({ grid, words_to_find, words, onSubmit }) => {
  const wordsToFind = words_to_find || words || [];
  const [foundWords, setFoundWords] = useState<Set<string>>(new Set());
  const [selectedCells, setSelectedCells] = useState<Set<string>>(new Set());

  const getCellKey = (row: number, col: number) => `${row}-${col}`;

  const handleCellClick = (row: number, col: number) => {
    const key = getCellKey(row, col);
    const newSelected = new Set(selectedCells);

    if (newSelected.has(key)) {
      newSelected.delete(key);
    } else {
      newSelected.add(key);
    }

    setSelectedCells(newSelected);

    // Check if selected cells form a word
    checkForWord(newSelected);
  };

  const checkForWord = (selected: Set<string>) => {
    const selectedArray = Array.from(selected).map(key => {
      const [row, col] = key.split('-').map(Number);
      return { row, col, letter: grid[row][col] };
    });

    // Sort by position to form a word
    selectedArray.sort((a, b) => {
      if (a.row !== b.row) return a.row - b.row;
      return a.col - b.col;
    });

    const word = selectedArray.map(cell => cell.letter).join('');

    // Check if this word is in our list
    const matchingWord = wordsToFind.find(w => w.word.toUpperCase() === word.toUpperCase());

    if (matchingWord && !foundWords.has(matchingWord.word)) {
      const newSet = new Set(foundWords);
      newSet.add(matchingWord.word);
      setFoundWords(newSet);
      // Keep cells selected to show the found word
    }
  };

  const handleClearSelection = () => {
    setSelectedCells(new Set());
  };

  const handleSubmit = () => {
    onSubmit(Array.from(foundWords));
  };

  const isComplete = foundWords.size === wordsToFind.length;

  return (
    <div className="space-y-6">
      <div className="alert alert-info">
        <svg xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" className="stroke-current shrink-0 w-6 h-6">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z" />
        </svg>
        <span>Find {wordsToFind.length} German words hidden in the grid</span>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
        {/* Word Grid */}
        <div>
          <h3 className="font-semibold mb-4">Letter Grid</h3>
          <div className="inline-block bg-base-200 p-4 rounded-lg">
            {grid.map((row, rowIndex) => (
              <div key={rowIndex} className="flex gap-1">
                {row.map((letter, colIndex) => {
                  const key = getCellKey(rowIndex, colIndex);
                  const isSelected = selectedCells.has(key);

                  return (
                    <button
                      key={colIndex}
                      onClick={() => handleCellClick(rowIndex, colIndex)}
                      className={`
                        btn btn-square btn-sm w-10 h-10 font-mono text-lg
                        ${isSelected ? 'btn-primary' : 'btn-outline'}
                      `}
                    >
                      {letter}
                    </button>
                  );
                })}
              </div>
            ))}
          </div>

          {selectedCells.size > 0 && (
            <button
              onClick={handleClearSelection}
              className="btn btn-ghost btn-sm mt-2"
            >
              Clear Selection
            </button>
          )}
        </div>

        {/* Words to Find */}
        <div>
          <h3 className="font-semibold mb-4">Words to Find ({foundWords.size}/{wordsToFind.length})</h3>
          <div className="space-y-2">
            {wordsToFind.map((wordItem, index) => {
              const isFound = foundWords.has(wordItem.word);

              return (
                <div
                  key={index}
                  className={`
                    p-3 rounded-lg border-2
                    ${isFound
                      ? 'border-success bg-success/10'
                      : 'border-base-300 bg-base-200'
                    }
                  `}
                >
                  <div className="flex items-center justify-between">
                    <div>
                      <span className={`font-semibold ${isFound ? 'line-through' : ''}`}>
                        {isFound ? wordItem.word : '???'}
                      </span>
                      <span className="text-sm text-base-content/70 ml-2">
                        ({wordItem.translation})
                      </span>
                    </div>
                    {isFound && (
                      <svg xmlns="http://www.w3.org/2000/svg" className="h-6 w-6 text-success" viewBox="0 0 20 20" fill="currentColor">
                        <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z" clipRule="evenodd" />
                      </svg>
                    )}
                  </div>
                </div>
              );
            })}
          </div>
        </div>
      </div>

      <div className="flex justify-center pt-4">
        <button
          onClick={handleSubmit}
          className="btn btn-primary btn-lg"
          disabled={!isComplete}
        >
          {isComplete
            ? 'Submit Answer'
            : `Find all words (${foundWords.size}/${wordsToFind.length})`
          }
        </button>
      </div>
    </div>
  );
};

export default WordHuntExercise;
